import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { isDateBeforeToday } from '../../../helpers';
import { NodeEvent } from '../../../types/uncinc_profile/nodes';

type EventsGroupedByMonthAndYear = Record<number, Record<string, ImmutableEntity<NodeEvent>[]>>;

const buildAgenda = (
  agenda: EventsGroupedByMonthAndYear,
  event: ImmutableEntity<NodeEvent>,
): EventsGroupedByMonthAndYear => {
  const dates = agenda;

  const eventEntity = event.toJS();
  const date = new Date(eventEntity.field_date[0].value);

  const year = date.getFullYear();
  const month = date.toLocaleString('nl-NL', { month: 'long' });

  // Add a date and month key to our dates object.
  dates[year] = dates[year] || {};
  dates[year]![month] = dates[year]![month] || [];

  dates[year]![month]!.push(event);

  return dates;
};

const withoutRecurrences = (
  entities: Immutable.List<ImmutableEntity<NodeEvent>>,
): EventsGroupedByMonthAndYear => {
  let dates: EventsGroupedByMonthAndYear = {};

  // Loop over all events and add them to our dates object
  entities.forEach((event: ImmutableEntity<NodeEvent>) => {
    const eventEntity = event.toJS();

    // We need to loop through the recurrences of the event and set the
    // date of our event to the first upcoming occurrence.
    let eventWithFirstOccurrence = event;
    let dateUpdated = false;
    eventEntity.field_date[0].dates.forEach((eventDate) => {
      const dateTime = new Date(eventDate.start.date);

      // Is this occurence before today, then we don't need it.
      if (isDateBeforeToday(dateTime)) {
        return;
      }

      // This if will only be hit once.
      if (!dateUpdated) {
        // Overwrite the 'field_date.value' value.
        // This is the value we need to render in our component.
        eventWithFirstOccurrence = event.setIn(
          ['field_date', 0, 'value'],
          eventDate.start.date,
        );

        dateUpdated = true;
      }
    });

    // Build our agenda.
    dates = buildAgenda(
      dates,
      eventWithFirstOccurrence,
    );
  });

  return dates;
};

const withRecurrences = (
  entities: Immutable.List<ImmutableEntity<NodeEvent>>,
): EventsGroupedByMonthAndYear => {
  let dates: EventsGroupedByMonthAndYear = {};

  // Loop over all events and add them to our dates object
  entities.forEach((event: ImmutableEntity<NodeEvent>) => {
    event.toJS().field_date[0].dates.forEach((eventDate) => {
      const dateTime = new Date(eventDate.start.date);

      // Skip events before midnight today.
      // We still want to list events that are happening today.
      if (isDateBeforeToday(dateTime)) {
        return;
      }

      // Overwrite the 'field_date.value' value.
      // This is the value we need to render in our component.
      const eventWithCurrentDate = event.setIn(
        ['field_date', 0, 'value'],
        eventDate.start.date,
      );

      // Build our agenda.
      dates = buildAgenda(
        dates,
        eventWithCurrentDate,
      );
    });
  });

  return dates;
};

interface GroupEventsProps {
  events: Immutable.List<ImmutableEntity<NodeEvent>>,
  showAllOccurrences?: boolean,
}

const groupEvents = (
  props: GroupEventsProps,
): EventsGroupedByMonthAndYear => {
  // In Drupal, a recurring event is still one event, with a list of recurrences.
  // However, here we would need separate items we can loop over and render.
  //
  // In the case of showAllOccurrences (true):
  // We loop over all recurrences in each event, and push that event to
  // a dates object, multiple times. But each time, with the date of that specific recurrence.
  //
  // In the case of !showAllOccurrences (false):
  // We loop over all recurrences in each event, and set the event date
  // to the first occurrence. We only push it to the dates object once.
  const dates: EventsGroupedByMonthAndYear = props.showAllOccurrences
    ? withRecurrences(props.events)
    : withoutRecurrences(props.events);

  return dates;
};

export default groupEvents;
